import { useEffect, useRef, useState, type FunctionComponent } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useNavigate } from 'react-router-dom';
import { identify, trackPageLoad } from '@fff-web/fff-analytics';
import { SubscriptionUpdatePaymentModal } from '@fff-web/fff-payments';
import { useUserContext } from '@fff-web/fff-utilities';
import AllErrorMessage from '../../components/AllErrorMessage';
import Layout from '../../components/Layout';
import { sendActivateSegmentEventAfterResponse } from '../../Events';
import { getPlanChangeCompletedEventStatusFromResponse } from '../../helpers';
import useScreenMode from '../../hooks/useScreenMode';
import useShowErrorOrRedirectThankYouPageV2 from '../../hooks/useShowErrorOrRedirectThankYouPageV2';
import { updateSubscription } from '../../services/subscription';
import { redirectToBaseSite } from '../../utils/redirectMethods';
import { MOBILE, Plans } from '../constants';
import env from '../MyAccount/utils/env';
import DesktopLayout from './DesktopLayout';
import MobileLayout from './MobileLayout';
import SubscribeTodayDisclaimer from './SubscribeTodayDisclaimer';
import MobileBg from './images/subscribe-today-bottom-bg@2x.png';
import * as styles from './subscribe-today.module.scss';

const THANKS_PAGE = '/reactivate-thanks';
const ROLE_CLUB = 'CLUB';

export const SubscribeToday: FunctionComponent = () => {
  const navigate = useNavigate();
  const socialCaptchaRef = useRef<HCaptcha>(null);
  const screenMode = useScreenMode();
  const [selectedPlanCode, setSelectedPlanCode] = useState(Plans.SEASONAL);
  const { userInfo } = useUserContext();
  const isClubMember = userInfo?.user?.role === ROLE_CLUB || false;

  const {
    isLoading,
    setLoading,
    showRetry,
    showPayment,
    errorPaymentMessage,
    onClose,
    handlePlanChangeResponse,
  } = useShowErrorOrRedirectThankYouPageV2(navigate, THANKS_PAGE);

  const handleChange = (membership) => setSelectedPlanCode(membership);

  const handleChangeSubscription = () => {
    socialCaptchaRef.current?.resetCaptcha();
    socialCaptchaRef.current
      ?.execute({ async: true })
      .then(({ response: captchaToken }) => {
        setLoading(true);
        handleActivateSubscription(captchaToken, selectedPlanCode);
      })
      .catch((err) => {
        console.error('Error processing you request:', err);
      });
  };

  const handleActivateSubscription = (captchaToken, upgradingPlan) => {
    let response = null;

    // make call to activate subscription
    updateSubscription(upgradingPlan, userInfo?.user?.planCode, [], captchaToken)
      .then((updateSubscriptionResponse) => {
        response = updateSubscriptionResponse;
        const status = getPlanChangeCompletedEventStatusFromResponse(response);
        const eventsToFire = [
          sendActivateSegmentEventAfterResponse(status, 'Confirm Purchase', 'Trial'),
        ];
        if (status === 'success') {
          eventsToFire.push(identify({ traits: { planCode: upgradingPlan } }));
        }
        return Promise.resolve(eventsToFire);
      })
      .then(() => {
        handlePlanChangeResponse(response);
      });
  };

  const purchaseStyle =
    screenMode === MOBILE
      ? {
          backgroundImage: `url(${MobileBg})`,
          backgroundPosition: 'center bottom',
          backgroundSize: 'cover',
        }
      : {};

  // allow access to club members
  useEffect(() => {
    if (isClubMember) {
      trackPageLoad({
        pathName: '/subscribe-today',
        properties: {
          role: (userInfo.user.role || '').toLowerCase(),
          subscription_state: (userInfo.user.subscriptionState || '').toLowerCase(),
        },
      }).then();
    } else {
      redirectToBaseSite();
    }
  }, [isClubMember]);

  return (
    <Layout
      pageTitle="Subscribe Today"
      isLoading={isLoading}
      showError={showRetry}
      ErrorComponent={() => <AllErrorMessage subjectAction="upgrade" onButtonClick={onClose} />}
    >
      <MobileLayout selectedPlan={selectedPlanCode} handlePlanChange={handleChange} />
      <DesktopLayout selectedPlan={selectedPlanCode} handlePlanChange={handleChange} />
      <section className={styles.purchase} style={purchaseStyle} data-testid="purchase-container">
        <SubscribeTodayDisclaimer />
        <button type="button" className={styles.confirm} onClick={handleChangeSubscription}>
          CONFIRM PURCHASE
        </button>
        <HCaptcha
          sitekey={env.hCaptchaSiteKey}
          onVerify={() => undefined}
          size="invisible"
          ref={socialCaptchaRef}
        />
      </section>
      {/* <HowItWorks /> */}
      <SubscriptionUpdatePaymentModal
        onClose={onClose}
        subtitle={errorPaymentMessage}
        isOpen={showPayment}
        onSuccess={handleChangeSubscription}
        hasError={!!errorPaymentMessage}
      />
    </Layout>
  );
};

export default SubscribeToday;
